import React from 'react';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import { rem } from 'design-system/values';
import Squircle from 'design-system/Squircle';
import FastImageCommon from 'common/FastImageCommon';
import useColors from 'hooks/useColors';
import useColorScheme from 'hooks/useColorScheme';
import { useAppSelector } from 'store/hooks';
import { selectMonthlyTotalsWithBudgets } from 'features/budgeting/selectors';
import { formatPlural } from 'utils';
import { IconCheckSquircle, IconCloseSquircle } from 'design-system/icons';
import { AnalyticsCardHeader } from './AnalyticsCardHeader';
import BudgetingCircleChart from './BudgetingCircleChart';
import BudgetingSetupButton from './BudgetingSetupButton';
const BudgetingCard = ({ thisMonth }) => {
    const paydayCountdown = useAppSelector((store) => store.feed.feed.cards.paydayCountdown);
    const thisMonthTotal = (thisMonth?.committed.amount || 0) + (thisMonth?.spending.amount || 0);
    const monthlyTotals = useAppSelector(selectMonthlyTotalsWithBudgets);
    const currentPeriodTotals = monthlyTotals.length ? monthlyTotals[0] : null;
    const rollingAmount = currentPeriodTotals?.rollingAccumulatedLimit;
    const totalBudget = currentPeriodTotals?.totalBudget;
    const totalSpend = currentPeriodTotals?.spending;
    const budget = thisMonth?.budget != null
        ? {
            amount: thisMonth?.budget,
            currency: thisMonth.income.currency,
        }
        : undefined;
    const spending = thisMonth?.spending;
    const totalSpending = {
        amount: thisMonthTotal,
        currency: thisMonth?.spending.currency,
    };
    const colors = useColors();
    const isDarkMode = useColorScheme() === 'dark';
    if (!budget) {
        return (<View style={styles.setupContainer} testID="budget-card">
        <Squircle width={rem(40)} height={rem(40)} color={colors.background.dark}>
          <FastImageCommon imageStyle={styles.helix} url={isDarkMode
                ? require('../../img/helix_dark.png')
                : require('../../img/helix.png')}/>
        </Squircle>
        <Text TextThin-12 Gray>
          Track your spending and save more, set up a budget.
        </Text>
        <BudgetingSetupButton />
      </View>);
    }
    let title = 'Budgeting';
    if (paydayCountdown) {
        const textDays = formatPlural('day', 'days', paydayCountdown.days);
        title = `${paydayCountdown.days} ${textDays} left`;
    }
    const isOnTarget = budget.amount >= totalSpending.amount;
    const committedBudgetRatio = totalSpending.amount / budget.amount;
    const spentBudgetRatio = (spending?.amount || 0) / budget.amount;
    const isNegativeRollover = rollingAmount != null && totalBudget != null && totalBudget <= 0;
    const amountThatWillRoll = totalBudget != null && totalSpend != null ? totalBudget + totalSpend : 0;
    const amount = Math.ceil(isNegativeRollover
        ? amountThatWillRoll
        : Math.abs(budget.amount - totalSpending.amount));
    const subTitle = (() => {
        if (isOnTarget) {
            return 'left of';
        }
        if (isNegativeRollover) {
            return 'will roll over';
        }
        return 'over by';
    })();
    return (<View testID="budget-card" style={styles.circularChartContainer}>
      <AnalyticsCardHeader title={title} icon={isOnTarget ? (<IconCheckSquircle colorKey="statusPositive" width={16} height={16}/>) : (<IconCloseSquircle colorKey="statusNegative" width={16} height={16}/>)} amount={{ amount, currency: totalSpending.currency || '' }} subTitle={subTitle}/>
      <View style={styles.content}>
        <BudgetingCircleChart spentRatio={spentBudgetRatio} committedRatio={committedBudgetRatio} isEmpty={isNegativeRollover}/>
        <View style={styles.totalContainer}>
          <Amount Numbers-16 showFraction={false} centered value={{
            value: isNegativeRollover ? 0 : Math.round(budget.amount),
            currency: budget.currency,
        }} removeZero/>
          <Text TextThin-12 Gray centered>
            Budget
          </Text>
        </View>
      </View>
    </View>);
};
const styles = StyleSheet.create({
    setupContainer: {
        flex: 1,
        justifyContent: 'space-between',
    },
    circularChartContainer: {
        flex: 1,
    },
    helix: {
        width: rem(25.78),
        height: rem(25.78),
    },
    content: {
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
    },
    totalContainer: {
        position: 'absolute',
    },
});
export default BudgetingCard;
